import {Nullable} from '../../../../Common/TypeHelper'
import React, {useState} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {BucketInfo} from '../../../DTO/BucketInfoDTO'
import {useLocation, useParams} from 'react-router-dom'
import {MailMagazineConfirmContentBlock} from '../../Components/ConfirmContentBlocks/MailMagazineConfirmContentBlock'
import {MBMemberError} from '../../../Common/MBMemberError'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type LocationStateForMemberEditMailMagazinePage = {
  bucketInfo: BucketInfo
  subscribeMailMagazineIdList: number[]
}

export function MembersEditMailMagazineConfirmPage(props: Props): Nullable<JSX.Element> {
  const {bucketKey} = useParams<{ bucketKey: string }>()
  const [processing, setProcessing] = useState<boolean>(false)

  const location = useLocation<LocationStateForMemberEditMailMagazinePage>()
  const locationState: LocationStateForMemberEditMailMagazinePage = location.state

  const throwMBMemberError = useMBMemberError()

  function isInvalidLocationState(
    locationState: LocationStateForMemberEditMailMagazinePage,
  ): boolean {
    if (!locationState) {
      return true
    }
    if (!locationState.bucketInfo) {
      return true
    }
    if (!locationState.subscribeMailMagazineIdList) {
      return true
    }
    if (!locationState.bucketInfo.mailMagazineList ||
      locationState.bucketInfo.mailMagazineList.length === 0) {
      return true
    }
    return false
  }

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  const {bucketInfo, subscribeMailMagazineIdList} = locationState

  async function goToEditMailMagazineCompletePage(): Promise<void> {
    setProcessing(true)
    try {
      const response =
        await props.memberRepo.putSubscribeMailMagazineIdList(
          bucketKey,
          subscribeMailMagazineIdList,
        )

      switch (response.status) {
        case CommonResponseStatus.Ok:
          props.navigator.goToEditMailMagazineCompletePage(bucketKey, bucketInfo)
          break
        case CommonResponseStatus.NotFound:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('NotFound')
        case CommonResponseStatus.InvalidRequest:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        case CommonResponseStatus.NoAuthority:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('SystemError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('UnknownError')
      }

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }


  return (
    <MembersPageTemplate
      hideFooterNavi={true}
      bucketInfo={bucketInfo}
    >
      <PageTitle>メルマガ情報変更確認</PageTitle>

      <StepGuide activity={'edit'} current={'confirm'}/>

      <MailMagazineConfirmContentBlock
        title={'メルマガ'}
        mailMagazineIdCheckedList={subscribeMailMagazineIdList}
        mailMagazineList={bucketInfo.mailMagazineList}
      />

      <MessageForAction>
        上記の内容に間違いがなければ<br/>『確定する』ボタンを押してください。
      </MessageForAction>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'determine-button'}
          disabled={processing}
          onClick={(): Promise<void> => goToEditMailMagazineCompletePage()}
        >
          確定する
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={(): void => props.navigator.goBack()}>
          メルマガ情報変更に戻る
        </PIMSecondaryButton>
      </PIMPageActions>
    </MembersPageTemplate>
  )
}
